import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useWatches } from "../../../context/watchProvider";
import LoadingBalls from "../../core/loadingBalls";
import { calculateRate, formatPhoneNumber, getAbsoluteUrl, parseTimestamp } from "../../../utils/functions";
import TemplateStars from "../../core/templateStars";
import Review from "../../core/review";
import ChevronDownIcon from "../../../img/svg/chevronDownIcon";
import InstagramIcon from "../../../img/svg/instagramIcon";
import SiteIcon from "../../../img/svg/siteIcon";
import WhatsappIcon from "../../../img/svg/whatsappIcon";
import { useAuth } from "../../../context/authProvider";

export default function Advertiser() {

    const { slug } = useParams();
    const { getAdvertiserBySlug, getPublicAdvertiserReviews, getAdvertiserWatches } = useWatches();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [advertiser, setAdvertiser] = useState<{ [key: string]: any } | null>(null);
    const [reviews, setReviews] = useState<{ rate: number, review: string, createdAt: Date }[] | null>(null);
    const [reviewIdx, setReviewIdx] = useState(0);
    const [watches, setWatches] = useState<{ [key: string]: any; id: string; }[]>([]);

    useEffect(() => {
        if (user) {
            doGetAdvertiser();
        }
        // eslint-disable-next-line
    }, [])

    const doGetAdvertiser = async () => {
        if (!slug) {
            return null;
        }
        setLoading(true);
        const adv = await getAdvertiserBySlug(slug);
        if (!adv) {
            setLoading(false);
            return null;
        }
        setAdvertiser(adv);
        const revs = await getPublicAdvertiserReviews(adv.id);
        if (revs) {
            setReviews(revs);
        }
        const wtcs = await getAdvertiserWatches(adv.id);
        if (!wtcs) {
            setLoading(false);
            return null;
        }
        setWatches(wtcs);
        setLoading(false);
    }

    const handleReviewPassage = (type: "prev" | "next") => {
        if (!reviews) {
            return null;
        }
        if (type === "next") {
            if (reviewIdx >= reviews.length - 1) {
                return null;
            }
            setReviewIdx(reviewIdx + 1);
        } else {
            if (reviewIdx <= 0) {
                return null;
            }
            setReviewIdx(reviewIdx - 1);
        }
    }

    if (loading) {
        return (
            <main className="h-screen">
                <div className="w-fit m-auto mt-10">
                    <LoadingBalls />
                </div>
            </main>
        )
    }

    if (!user || !user?.emailVerified || !user?.termsAccepted?.accepted) {
        return (
            <main className="max-w-7xl mx-auto px-4 md:px-10 py-24 mb-16 h-screen flex justify-center items-center">
                <div className="w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl border border-surface p-12 text-center animate-superfade">
                    <h2 className="text-2xl font-black text-primary tracking-tight mb-4">Acesso Restrito</h2>
                    <p className="text-lg text-mono mb-8">
                        {
                            !user ? "Faça login para ver as informações do anunciante." :
                                !user?.emailVerified ?
                                    "Verifique seu email para ver as informações do anunciante." :
                                    !user.termsAccepted?.accepted ?
                                        "Aceite os termos de uso para ver as informações do anunciante." : "Dados do anunciante indisponíveis."
                        }
                    </p>
                    <button
                        onClick={() => navigate(!user ? "/gatekeeper" : "/me", { state: { from: `/advertisers/${slug}` } })}
                        className="px-10 py-4 bg-primary text-secondary font-black tracking-widest text-xs rounded-2xl hover:opacity-90 transition-all shadow-xl active:scale-95"
                    >
                        {!user ? "ENTRAR" : "PROSSEGUIR"}
                    </button>
                </div>
            </main>
        )
    }

    if (!advertiser) {
        return (
            <main className="max-w-7xl mx-auto px-4 md:px-10 py-24 mb-16 h-[50vh] flex flex-col justify-center items-center animate-superfade">
                <h2 className="text-2xl font-black text-primary tracking-tight mb-4">Anunciante não encontrado!</h2>
                <p className="text-mono">Verifique se o link está correto.</p>
            </main>
        )
    }

    return (
        <main className="max-w-7xl mx-auto px-4 md:px-10 py-24 mb-16 animate-superfade">
            <div className="flex flex-col md:flex-row justify-between items-start mb-16 gap-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="w-24 h-24 bg-moninho rounded-full flex items-center justify-center text-3xl font-black text-primary border-4 border-white shadow-xl">
                        {advertiser?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-center md:text-left">
                        <div className="flex flex-col md:flex-row items-center gap-3 mb-2">
                            <h1 className="text-3xl font-black text-primary tracking-tight">
                                {advertiser?.name}
                            </h1>
                            {reviews && <TemplateStars width="20px" rate={calculateRate(reviews)} />}
                        </div>
                        <p className="text-mono text-sm tracking-wide">Anunciante desde {parseTimestamp(advertiser?.createdAt)}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-surface">
                    {advertiser?.phoneNumber && (
                        <a href={`https://wa.me/55${advertiser?.phoneNumber}`} target="_blank" rel="noreferrer" className="w-12 h-12 flex items-center justify-center bg-moninho rounded-xl hover:bg-[#25D366] hover:text-white transition-all text-primary group">
                            <WhatsappIcon />
                        </a>
                    )}
                    {advertiser?.instagram && (
                        <a href={`https://instagram.com/${advertiser?.instagram}`} target="_blank" rel="noreferrer" className="w-12 h-12 flex items-center justify-center bg-moninho rounded-xl hover:bg-[#E1306C] hover:text-white transition-all text-primary group">
                            <InstagramIcon />
                        </a>
                    )}
                    {advertiser?.site && (
                        <a href={`${getAbsoluteUrl(advertiser?.site)}`} target="_blank" rel="noreferrer" className="w-12 h-12 flex items-center justify-center bg-moninho rounded-xl hover:bg-primary hover:text-white transition-all text-primary group">
                            <SiteIcon />
                        </a>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-1 space-y-10">
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-primary/5 border border-primary/5">
                        <h2 className="text-sm font-black tracking-widest uppercase text-secondary mb-6">Informações</h2>
                        <div className="space-y-6 divide-y divide-surface">
                            <div className="pt-0">
                                <p className="text-[10px] font-black text-mono tracking-[0.2em] uppercase mb-1">Email</p>
                                <p className="font-bold text-primary break-all">{advertiser?.email}</p>
                            </div>
                            <div className="pt-6">
                                <p className="text-[10px] font-black text-mono tracking-[0.2em] uppercase mb-1">Telefone</p>
                                <a href={`https://wa.me/55${advertiser?.phoneNumber}`} target="_blank" rel="noreferrer" className="font-bold text-secondary hover:underline">
                                    {formatPhoneNumber(advertiser?.phoneNumber)}
                                </a>
                            </div>
                            {advertiser?.description && (
                                <div className="pt-6">
                                    <p className="text-[10px] font-black text-mono tracking-[0.2em] uppercase mb-2">Descrição</p>
                                    <p className="text-sm text-primary leading-relaxed">{advertiser?.description}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {reviews && reviews.length > 0 && (
                        <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-primary/5 border border-primary/5">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-sm font-black tracking-widest uppercase text-secondary">Avaliações ({reviews.length})</h2>
                                <div className="flex gap-2">
                                    <button onClick={() => handleReviewPassage("prev")} disabled={reviewIdx <= 0} className="w-8 h-8 flex items-center justify-center bg-moninho rounded-full disabled:opacity-30 transform rotate-90 text-primary transition-all hover:bg-surface">
                                        <ChevronDownIcon width="16px" fill="currentColor" />
                                    </button>
                                    <button onClick={() => handleReviewPassage("next")} disabled={reviewIdx >= reviews.length - 1} className="w-8 h-8 flex items-center justify-center bg-moninho rounded-full disabled:opacity-30 transform -rotate-90 text-primary transition-all hover:bg-surface">
                                        <ChevronDownIcon width="16px" fill="currentColor" />
                                    </button>
                                </div>
                            </div>
                            <div className="bg-moninho p-6 rounded-2xl h-[120px] overflow-y-auto custom-scrollbar">
                                <Review review={reviews[reviewIdx]} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2">
                    <h2 className="text-2xl font-black text-primary tracking-tight mb-8">Acervo do Anunciante</h2>
                    {watches && watches.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {watches.map((w, i) => (
                                <Link
                                    to={`/watches/${w.slug}`}
                                    key={i}
                                    className="flex flex-col bg-white rounded-[2rem] border border-surface shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden group"
                                >
                                    <div className="relative w-full aspect-square bg-moninho border-b border-surface overflow-hidden">
                                        <img
                                            src={w.cImgUrl}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            alt={w.model}
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col justify-center text-center">
                                        <p className="text-[10px] font-bold text-mono tracking-[0.2em] uppercase mb-1 truncate">
                                            {w.brand} | {w.year}
                                        </p>
                                        <h3 className="text-lg font-black text-primary group-hover:text-secondary transition-colors mb-2 truncate">
                                            {w.model.toUpperCase()}
                                        </h3>
                                        <p className="text-lg font-light text-secondary">
                                            R${w.price.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-moninho rounded-3xl border border-dashed border-mono/30 text-mono">
                            <p className="text-lg uppercase tracking-widest font-bold">Nenhum relógio encontrado</p>
                            <p className="text-sm">Este anunciante não possui itens ativos no momento.</p>
                        </div>
                    )}
                </div>
            </div>
        </main>
    )
}
